/* ==========================================================================
   Winstreak — shared JS for ws-* sections
   - Purchase module: flavor / pack-size / subscription state + price recompute
   - FAQ accordion open/close
   - Announcement bar: tap-to-toggle fallback for touch devices
   ========================================================================== */

import { CartLinesUpdateEvent } from '@shopify/events';

(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * 1. Purchase module
   * ------------------------------------------------------------------ */

  function formatMoney(amount) {
    return '$' + amount.toFixed(2);
  }

  /* Parses a size button's data-selling-plans attribute, formatted as
     "Label::id||Label::id" — each pack size has its own selling plan IDs,
     since plans are scoped per variant, not shared across pack sizes. */
  function parseSellingPlans(raw) {
    var map = {};
    if (!raw) return map;
    raw.split('||').forEach(function (pair) {
      var parts = pair.split('::');
      if (parts.length === 2 && parts[0]) map[parts[0]] = parts[1];
    });
    return map;
  }

  function initPurchaseModule(root) {
    var flavorButtons = root.querySelectorAll('[data-ws-flavor]');
    var sizeButtons = root.querySelectorAll('[data-ws-size]');
    var deliveryButtons = root.querySelectorAll('[data-ws-delivery]');
    var cadenceSelect = root.querySelector('[data-ws-cadence]');
    var submitBtn = root.querySelector('[data-ws-submit]');

    var flavorInput = root.querySelector('[data-ws-flavor-input]');
    var sizeInput = root.querySelector('[data-ws-size-input]');
    var sticksInput = root.querySelector('[data-ws-sticks-input]');
    var deliveryInput = root.querySelector('[data-ws-delivery-input]');
    var cadenceInput = root.querySelector('[data-ws-cadence-input]');
    var variantInput = root.querySelector('[data-ws-variant-id]');
    var sellingPlanInput = root.querySelector('[data-ws-selling-plan-input]');
    var errorEl = root.querySelector('[data-ws-error]');

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    function hideError() {
      if (!errorEl) return;
      errorEl.hidden = true;
    }

    function setPressed(group, target) {
      group.forEach(function (btn) {
        btn.setAttribute('aria-pressed', btn === target ? 'true' : 'false');
      });
    }

    function activeButton(group) {
      var active = null;
      group.forEach(function (btn) {
        if (btn.getAttribute('aria-pressed') === 'true') active = btn;
      });
      return active || group[0];
    }

    function recompute() {
      var size = activeButton(sizeButtons);
      var delivery = activeButton(deliveryButtons);
      var flavor = activeButton(flavorButtons);

      if (!size || !delivery) return;

      var basePrice = parseFloat(size.getAttribute('data-price')) || 0;
      var sticks = parseFloat(size.getAttribute('data-sticks')) || 1;
      var packCount = parseFloat(size.getAttribute('data-pack-count')) || 1;
      var subscribing = delivery.hasAttribute('data-subscribe');
      var basePerStick = parseFloat(root.getAttribute('data-base-per-stick')) || (basePrice / sticks);

      /* Subscription price = the pack's selling plan (renewal) price minus its
         first-order intro discount, which is a first-payment-only automatic
         discount at checkout. One-time purchase uses the variant's own price. */
      var renewalPrice = parseFloat(size.getAttribute('data-renewal-price')) || basePrice;
      var introDiscount = parseFloat(size.getAttribute('data-intro-discount')) || 0;
      function priceForDelivery(btn) {
        return btn.hasAttribute('data-subscribe') ? renewalPrice - introDiscount : basePrice;
      }

      /* Update the size button's own displayed price (base, not discounted) */
      sizeButtons.forEach(function (btn) {
        var priceEl = btn.querySelector('[data-ws-size-price]');
        if (priceEl) {
          priceEl.textContent = formatMoney(parseFloat(btn.getAttribute('data-price')) || 0);
        }
      });

      /* Update each delivery option's price preview against the *current* size */
      deliveryButtons.forEach(function (btn) {
        var t = priceForDelivery(btn);
        var ps = t / sticks;
        var perServingEl = btn.querySelector('[data-ws-delivery-per-serving]');
        if (perServingEl) {
          perServingEl.textContent = formatMoney(ps);
        }
        var totalEl = btn.querySelector('[data-ws-delivery-total]');
        if (totalEl) {
          totalEl.textContent = formatMoney(t);
        }
        /* Compare-at (strikethrough) price is always the flat single-pack
           rate per serving ($/stick at 1-pack) — e.g. a 2-pack compares
           against the 1-pack rate, not against its own already-bulk-discounted
           one-time price. Hidden when there's no gap (the 1-pack size itself,
           where compare == actual). */
        var compareTotal = basePerStick * sticks;
        var compareEl = btn.querySelector('[data-ws-delivery-compare]');
        if (compareEl) {
          compareEl.textContent = formatMoney(basePerStick);
          compareEl.hidden = compareTotal <= t;
        }
        /* "Save X%" badge (one-time purchase only) — the bulk-pack discount
           already baked into this size's price vs. the flat 1-pack rate. */
        var badgeEl = btn.querySelector('[data-ws-delivery-badge]');
        if (badgeEl) {
          var savingsPercent = compareTotal > 0 ? Math.round((1 - t / compareTotal) * 100) : 0;
          badgeEl.textContent = 'Save ' + savingsPercent + '%';
          badgeEl.hidden = savingsPercent <= 0;
        }
        /* Subscription badge + "then $X every N weeks" renewal line. */
        var subBadgeEl = btn.querySelector('[data-ws-sub-badge]');
        if (subBadgeEl) {
          var subSavings = compareTotal > 0 ? Math.round((1 - t / compareTotal) * 100) : 0;
          subBadgeEl.textContent = 'Save ' + subSavings + '%' + (introDiscount > 0 ? ' today' : '');
        }
        var renewalEl = btn.querySelector('[data-ws-sub-renewal]');
        if (renewalEl) {
          var cadenceText = cadenceSelect ? cadenceSelect.value.toLowerCase() : '';
          var renewalSavings = compareTotal > 0 ? Math.round((1 - renewalPrice / compareTotal) * 100) : 0;
          var renewalMoney = renewalPrice % 1 === 0 ? '$' + renewalPrice : formatMoney(renewalPrice);
          renewalEl.textContent = 'then ' + renewalSavings + '% off (' + renewalMoney + ')' + (cadenceText ? ' ' + cadenceText : '');
          renewalEl.hidden = introDiscount <= 0;
        }
      });

      if (flavorInput && flavor) flavorInput.value = flavor.getAttribute('data-value') || flavor.textContent.trim();
      if (sizeInput) sizeInput.value = packCount;
      if (sticksInput) sticksInput.value = sticks;
      if (deliveryInput) deliveryInput.value = delivery.getAttribute('data-value') || (subscribing ? 'Subscribe & Save' : 'One-Time Purchase');

      if (variantInput) {
        variantInput.value = size.getAttribute('data-variant-id') || '';
      }

      /* Selling plan IDs are scoped per pack size (variant), so the plan for
         the currently active size button must be looked up by cadence label
         every time — the same cadence label maps to a different plan ID
         depending on which pack size is selected. */
      var isSubscribing = subscribing && cadenceSelect;
      var sellingPlanId = '';
      if (isSubscribing) {
        var plans = parseSellingPlans(size.getAttribute('data-selling-plans') || '');
        sellingPlanId = plans[cadenceSelect.value] || '';
      }

      /* Cadence is only meaningful for a subscription — omit it entirely for
         one-time purchases instead of leaving a stale/irrelevant value. The
         cadence picker itself stays visible and interactive either way, so
         nothing in the layout shifts when switching delivery options. */
      if (cadenceInput) {
        cadenceInput.disabled = !isSubscribing;
        cadenceInput.value = isSubscribing ? cadenceSelect.value : '';
      }
      if (sellingPlanInput) {
        /* Only submit selling_plan when a valid plan was resolved — Shopify
           creates a subscription line item whenever this param is present
           at all, so it must be fully excluded (not just emptied) otherwise. */
        sellingPlanInput.disabled = !sellingPlanId;
        sellingPlanInput.value = sellingPlanId;
      }

      var isAvailable = size.getAttribute('data-available') === 'true' && (!isSubscribing || !!sellingPlanId);
      if (submitBtn) {
        submitBtn.disabled = !isAvailable;
        var label = submitBtn.getAttribute('data-ws-label') || 'Add to cart';
        submitBtn.textContent = isAvailable
          ? label
          : (isSubscribing && !sellingPlanId ? 'Unavailable for this plan' : 'Sold out');
      }
    }

    flavorButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        setPressed(flavorButtons, btn);
        recompute();
      });
    });

    sizeButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        setPressed(sizeButtons, btn);
        recompute();
      });
    });

    deliveryButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        setPressed(deliveryButtons, btn);
        recompute();
      });
    });

    if (cadenceSelect) {
      cadenceSelect.addEventListener('change', recompute);
    }

    /* Add to cart over AJAX instead of a full-page form submit, dispatching the
       same shopify:cart:lines-update event Horizon's own product forms use —
       this is what makes the native cart drawer auto-open, the header cart
       count update, and the cart line items re-render, all for free. */
    root.addEventListener('submit', function (event) {
      event.preventDefault();
      if (submitBtn && submitBtn.disabled) return;

      hideError();

      var formData = new FormData(root);
      var variantId = formData.get('id');

      var sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach(function (el) {
        if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
      });
      formData.append('sections', sectionIds.join(','));

      var deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity: 1 }],
          promise: deferred.promise
        })
      );

      if (submitBtn) submitBtn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      })
        .then(function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        })
        .then(function (result) {
          return fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              var didError = !result.ok || !!result.data.status;
              if (didError) {
                showError(result.data.description || result.data.message || 'Something went wrong. Please try again.');
              }
              deferred.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
                detail: {
                  didError: didError,
                  items: cart.items,
                  source: 'ws-purchase',
                  itemCount: cart.item_count,
                  sections: didError ? undefined : result.data.sections
                }
              });
            });
        })
        .catch(function (err) {
          showError('Network error — please try again.');
          deferred.reject(err);
        })
        .finally(function () {
          if (submitBtn) submitBtn.disabled = false;
          recompute();
        });
    });

    recompute();
  }

  /* ------------------------------------------------------------------ *
   * 2. FAQ accordion
   * ------------------------------------------------------------------ */

  function initFaq(root) {
    var items = root.querySelectorAll('[data-ws-faq-item]');

    items.forEach(function (item) {
      var question = item.querySelector('[data-ws-faq-question]');
      if (!question) return;

      question.addEventListener('click', function () {
        var isOpen = item.getAttribute('data-state') === 'open';

        /* Close all other items (single-open accordion, matches source) */
        items.forEach(function (other) {
          if (other === item) return;
          other.setAttribute('data-state', 'closed');
          var otherQ = other.querySelector('[data-ws-faq-question]');
          if (otherQ) otherQ.setAttribute('aria-expanded', 'false');
        });

        var next = isOpen ? 'closed' : 'open';
        item.setAttribute('data-state', next);
        question.setAttribute('aria-expanded', next === 'open' ? 'true' : 'false');
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 3. Hero photo gallery — click a thumbnail to swap the main image
   * ------------------------------------------------------------------ */

  function initHeroGallery(root) {
    var mainImage = root.querySelector('[data-ws-hero-main-image]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-ws-hero-thumb]'));
    var prevBtn = root.querySelector('[data-ws-hero-prev]');
    var nextBtn = root.querySelector('[data-ws-hero-next]');
    if (!mainImage || !thumbs.length) return;

    var currentIndex = 0;
    thumbs.forEach(function (t, i) {
      if (t.classList.contains('is-active')) currentIndex = i;
    });

    function showSlide(index) {
      var count = thumbs.length;
      var next = ((index % count) + count) % count;
      var thumb = thumbs[next];
      var fullSrc = thumb.getAttribute('data-full-src');
      if (!fullSrc) return;

      mainImage.src = fullSrc;
      mainImage.removeAttribute('srcset');
      mainImage.alt = thumb.getAttribute('data-alt') || mainImage.alt;

      thumbs.forEach(function (t) {
        t.classList.remove('is-active');
        t.setAttribute('aria-pressed', 'false');
      });
      thumb.classList.add('is-active');
      thumb.setAttribute('aria-pressed', 'true');

      currentIndex = next;
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () {
        showSlide(i);
      });
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        showSlide(currentIndex - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        showSlide(currentIndex + 1);
      });
    }
  }

  /* ------------------------------------------------------------------ *
   * 4. Announcement bar — tap-to-toggle fallback for touch devices
   * ------------------------------------------------------------------ */

  /* Works anywhere on the page — announcement bar, footer, or future
     sections — any element with data-ws-klaviyo-trigger + a
     data-klaviyo-form-id opens that Klaviyo popup on click. */
  function initKlaviyoTriggers(root) {
    var triggers = root.querySelectorAll('[data-ws-klaviyo-trigger]');
    triggers.forEach(function (klaviyoTrigger) {
      klaviyoTrigger.addEventListener('click', function () {
        var formId = klaviyoTrigger.getAttribute('data-klaviyo-form-id');
        if (!formId) return;
        /* Klaviyo's documented API for programmatically opening an onsite
           form — queues onto _klOnsite so it works even if klaviyo.js
           hasn't finished loading yet. */
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(['openForm', formId]);
      });
    });
  }

  function initAnnouncementBar(root) {
    var trigger = root.querySelector('[data-ws-announcement-trigger]');
    var flyout = root.querySelector('[data-ws-announcement-flyout]');
    if (!trigger || !flyout) return;

    function close() {
      flyout.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
    }

    trigger.addEventListener('click', function (event) {
      /* Only needed as a fallback where :hover doesn't apply (touch) */
      if (!window.matchMedia('(hover: hover)').matches) {
        event.preventDefault();
        var isOpen = flyout.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }
    });

    document.addEventListener('click', function (event) {
      if (!root.contains(event.target)) close();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') close();
    });
  }

  /* ------------------------------------------------------------------ *
   * 5. Footer policy links — open as a popup dialog instead of navigating
   * ------------------------------------------------------------------ */

  function initPolicyModals(root) {
    var triggers = root.querySelectorAll('[data-ws-policy-trigger]');

    triggers.forEach(function (trigger) {
      var key = trigger.getAttribute('data-ws-policy-trigger');
      var modal = document.querySelector('[data-ws-policy-modal="' + key + '"]');
      if (!modal) return;

      trigger.addEventListener('click', function () {
        modal.showModal();
      });

      var closeBtn = modal.querySelector('[data-ws-policy-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          modal.close();
        });
      }

      /* Close on backdrop click (native <dialog> click target is the dialog
         itself when the click lands outside its content box). */
      modal.addEventListener('click', function (event) {
        if (event.target === modal) modal.close();
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  function boot() {
    document.querySelectorAll('[data-ws-purchase]').forEach(initPurchaseModule);
    document.querySelectorAll('[data-ws-faq]').forEach(initFaq);
    document.querySelectorAll('[data-ws-hero-gallery]').forEach(initHeroGallery);
    document.querySelectorAll('[data-ws-announcement]').forEach(initAnnouncementBar);
    initKlaviyoTriggers(document);
    initPolicyModals(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Re-init when sections are added/re-rendered in the theme customizer */
  document.addEventListener('shopify:section:load', function (event) {
    var el = event.target;
    if (!el) return;
    el.querySelectorAll('[data-ws-purchase]').forEach(initPurchaseModule);
    el.querySelectorAll('[data-ws-faq]').forEach(initFaq);
    el.querySelectorAll('[data-ws-hero-gallery]').forEach(initHeroGallery);
    el.querySelectorAll('[data-ws-announcement]').forEach(initAnnouncementBar);
    initKlaviyoTriggers(el);
    if (el.querySelector('[data-ws-policy-trigger]')) initPolicyModals(el);
  });
})();
